import { useEffect, useState, useCallback } from 'react';
import $ from 'jquery';
import * as Sentry from '@sentry/browser';

const pageSize = 50;

const useSearch = ({ query, resultsQuery, facetsQuery, cache }) => {
  const [loadingResults, setLoadingResults] = useState(false);
  const [results, setResults] = useState(
    query.length === 0 ? [] : cache.results
  );
  const [enhancedResults, setEnhancedResults] = useState(
    query.length === 0 ? [] : cache.enhancedResults
  );
  const [projectFacets, setProjectFacets] = useState(cache.projectFacets);
  const [searchableTypeFacets, setSearchableTypeFacets] = useState(
    cache.searchableTypeFacets
  );
  const [customProjectFacet, setCustomProjectFacet] = useState(
    cache.customProjectFacet
  );
  const [customSearchableTypeFacet, setCustomSearchableTypeFacet] = useState(
    cache.customSearchableTypeFacet
  );
  const [workflowStatusFacets, setWorkflowStatusFacets] = useState(
    cache.workflowStatusFacets
  );
  const [customWorkflowStatusFacet, setCustomWorkflowStatusFacet] = useState(
    cache.customWorkflowStatusFacet
  );
  const [assignedToUserIdFacets, setAssignedToUserIdFacets] = useState(
    cache.assignedToUserIdFacets
  );
  const [fetchingMore, setFetchingMore] = useState(false);

  useEffect(() => {
    if (cache.query !== query) {
      cache.query = query;
      cache.results = [];
      cache.enhancedResults = [];
      setResults([]);
      setEnhancedResults([]);
    }
  }, [query, cache.query, cache.results, cache.enhancedResults]);

  const fetchMore = useCallback(() => {
    if (cache.currentPage === cache.maxPage || fetchingMore) return;

    setFetchingMore(true);
    cache.fetchMoreXhr = $.ajax({
      url: `/search`,
      type: 'GET',
      data: {
        page: (cache.currentPage += 1),
        per_page: pageSize,
        ...resultsQuery({
          customSearchableTypeFacet,
          customProjectFacet,
          customWorkflowStatusFacet,
        }),
      },
      dataType: 'json',
    });
    cache.fetchMoreXhr.then(response => {
      setFetchingMore(false);
      cache.fetchMoreXhr = null;
      const newResults = Array.isArray(response) ? response : response.results;
      if (newResults.length < pageSize) {
        cache.maxPage = cache.currentPage;
      }

      const moreResults = [...results, ...newResults];
      setResults(moreResults);
      cache.results = moreResults;
    });
  }, [
    fetchingMore,
    resultsQuery,
    customSearchableTypeFacet,
    customProjectFacet,
    customWorkflowStatusFacet,
    results,
    cache,
  ]);

  useEffect(() => {
    if (query.length > 1 && results.length === 0) {
      // Load results
      setLoadingResults(true);

      if (cache.xhr) {
        cache.xhr.abort();
      }

      if (cache.fetchMoreXhr) {
        cache.fetchMoreXhr.abort();
      }

      cache.xhr = $.ajax({
        url: `/search`,
        type: 'GET',
        data: {
          page: 1,
          per_page: pageSize,
          ...resultsQuery({
            customSearchableTypeFacet,
            customProjectFacet,
            customWorkflowStatusFacet,
          }),
        },
        dataType: 'json',
      });

      // In some cases, we would get "zombie" custom facet IDs where the ID does not belong to any of
      // the available facets, but is still included in the query. For example:
      //
      // 1. User types "feat", gets "feature 1" (in progress) and "feature 2" (not started)
      // 2. User filters down to "in progress", showing only feature 1
      // 3. User continues typing "feat 2", showing no results (or at least none matching the filter)
      //
      // At this point, the facets section might go away and there would be no way to un-filter "in progress"
      // until they put in a search query that brings up matching results.
      //
      // This `keepId` function ensures that any time the available facets change, they're
      // guaranteed to keep facets matching the custom facet ID, even if no results currently match
      // it and it's not included in the server response. This allows the user to un-filter any
      // selected facets no matter what.
      const keepId = (facets, customFacetId) => prevFacets => {
        if (customFacetId) {
          const customFacetIds = customFacetId.split(',');
          const matches = f => {
            const ids = (f[0].id ?? f[0].key)?.split(',');
            return ids && customFacetIds.some(id => ids.includes(id));
          };
          // if the current results set of facets doesn't include the previously selected facet...
          if (!facets.some(matches)) {
            const prevFacetToKeep = prevFacets.find(matches);
            if (prevFacetToKeep) {
              // ...pop it back in from memory, with 0 matches
              return [...facets, [prevFacetToKeep[0], 0]];
            }
          }
        }
        return facets;
      };

      cache.xhr.then(response => {
        cache.xhr = null;
        setLoadingResults(false);
        if (response.results) {
          setResults(response.results);
          cache.results = response.results;

          setProjectFacets(keepId(response.project_facets, customProjectFacet));
          setSearchableTypeFacets(
            keepId(response.searchable_type_facets, customSearchableTypeFacet)
          );
          setWorkflowStatusFacets(
            keepId(response.workflow_status_facets, customWorkflowStatusFacet)
          );
          setAssignedToUserIdFacets(response.assigned_to_user_facets);
          cache.projectFacets = response.project_facets;
          cache.searchableTypeFacets = response.searchable_type_facets;
          cache.workflowStatusFacets = response.workflow_status_facets;
          cache.assignedToUserIdFacets = response.assigned_to_user_id_facets;
        } else {
          setResults(response);
          cache.results = response;
        }

        cache.maxPage = null;
        cache.currentPage = 1;

        if (cache.results.length < pageSize) {
          cache.maxPage = cache.currentPage;
        }
      });

      if (cache.enhancedXhr) {
        cache.enhancedXhr.abort();
      }

      cache.enhancedXhr = $.ajax({
        url: `/search`,
        type: 'GET',
        data: {
          ...resultsQuery({
            customSearchableTypeFacet,
            customProjectFacet,
            customWorkflowStatusFacet,
          }),
          include_facets: false,
          enhanced: true,
          page: 0,
          per_page: 20,
        },
        dataType: 'json',
      });

      cache.enhancedXhr
        .then(response => {
          cache.enhancedXhr = null;
          setEnhancedResults(response);
          cache.enhancedResults = response;
        })
        .fail((jqxhr, textStatus, errorThrown) => {
          cache.enhancedXhr = null;
          const IGNORED_ERRORS = ['abort', 'forbidden'];
          if (!IGNORED_ERRORS.includes(errorThrown)) {
            Sentry.captureException(
              new Error(
                `Error fetching enhanced search results, textStatus: ${textStatus}, errorThrown: ${errorThrown}`
              )
            );
          }
        });
    }
  }, [
    query,
    customSearchableTypeFacet,
    customProjectFacet,
    customWorkflowStatusFacet,
    setLoadingResults,
    facetsQuery,
    resultsQuery,
    cache,
    results.length,
  ]);

  useEffect(() => {
    // Clear results if facets change
    if (
      cache.customSearchableTypeFacet() !== customSearchableTypeFacet ||
      cache.customProjectFacet() !== customProjectFacet ||
      cache.customWorkflowStatusFacet() !== customWorkflowStatusFacet
    ) {
      cache.results = [];
      setResults([]);
    }

    // and cache new facets
    cache.customSearchableTypeFacet = () => {
      return customSearchableTypeFacet;
    };
    cache.customProjectFacet = () => {
      return customProjectFacet;
    };

    cache.customWorkflowStatusFacet = () => {
      return customWorkflowStatusFacet;
    };
  }, [
    customSearchableTypeFacet,
    customProjectFacet,
    customWorkflowStatusFacet,
    cache,
  ]);

  return {
    results,
    enhancedResults,
    projectFacets,
    searchableTypeFacets,
    customProjectFacet,
    workflowStatusFacets,
    setCustomProjectFacet,
    customSearchableTypeFacet,
    setCustomSearchableTypeFacet,
    setCustomWorkflowStatusFacet,
    customWorkflowStatusFacet,
    assignedToUserIdFacets,
    setAssignedToUserIdFacets,
    loadingResults,
    fetchMore,
    fetchingMore,
  };
};

export default useSearch;
